import React, { useEffect, useState } from 'react';
import { CalendarCheck2, ChevronDown, ChevronUp, PartyPopper, Square, SquareCheck } from 'lucide-react';
import { injectIntl } from 'react-intl';
import { QUEUE_PREVIEW_SIZE, QUEUE_SIGNALS, SIGNAL_COLOR, SIGNAL_MESSAGE_ID } from '../../../../constants/client-queue';
import { adviserFactsheetOf } from '../../../../utils/client-factsheet';
import type { QueueSignal } from '../../../../constants/client-queue';
import type { QueueEntry } from '../../../../utils/client-queue';
import { avatarGenderClass, clientNameOf, tierMetaOf } from '../clientPresentation';
import { useCountUp } from './StatTiles';
import type { IntlLike } from '../clientPresentation';
import type { FactsFillIn } from '../factsFillIn';
import { CLIENT_NOTES_CHANGED_EVENT } from '../clientNotesStore';
import { readServicePrefs, servicePrefsSummary } from '../../../CoverQuiz/coverServicePrefs';
import { quizLangOf } from '../../../CoverQuiz/quizLang';
import type { CpiField } from '../clientCpiGap';

export type TodayQueueProps = {
  entries: QueueEntry[];
  adviserEmail?: string;
  onSelectClient: (clientId: string) => void;
  /**
   * 補資料 — offered only when the dated signals leave a slot free, never in
   * place of one (factsFillIn.ts).
   */
  fillIn?: FactsFillIn | null;
  onFillIn?: (fill: FactsFillIn) => void;
  /** 「暫時不知道」: the client leaves this slot for FILL_IN_SNOOZE_DAYS. */
  onSnoozeFillIn?: (fill: FactsFillIn) => void;
  intl: IntlLike;
};

/** Slate: a chore, quieter than the four dated signals, and clear of the app's own blue. */
const FILL_IN_COLOR = '#475569';

const FILL_IN_FIELD_ID: Record<CpiField, string> = {
  personality: 'clientv2-queue-fill-field-personality',
  marriageStatus: 'clientv2-queue-fill-field-marriage',
  child: 'clientv2-queue-fill-field-child',
  dob: 'clientv2-queue-fill-field-dob',
  monthlyIncome: 'clientv2-queue-fill-field-income'
};

const SIGNAL_LABEL_ID: Record<QueueSignal, string> = {
  [QUEUE_SIGNALS.REMIND_TODAY]: 'clientv2-queue-signal-reminder',
  [QUEUE_SIGNALS.BIRTHDAY_IMMINENT]: 'clientv2-queue-signal-birthday',
  [QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH]: 'clientv2-queue-signal-birthday',
  [QUEUE_SIGNALS.POLICY_DUE_SOON]: 'clientv2-queue-signal-policy',
  [QUEUE_SIGNALS.LONG_TIME_NO_SEE]: 'clientv2-queue-signal-reconnect'
};

const entryKeyOf = (entry: QueueEntry): string => `${entry.client._id}-${entry.signal}`;

/**
 * When, said with its subject: 「明天生日」, not a bare 「1 天後」. This used to
 * sit inside the tick box, which made the box read as a date label rather than
 * something to tick; it now sits under the name, as on the recruitment card.
 */
const whenTextOf = (entry: QueueEntry, intl: IntlLike): string => {
  const { daysUntil, daysSince } = entry.meta;
  switch (entry.signal) {
    case QUEUE_SIGNALS.REMIND_TODAY:
      return intl.formatMessage({ id: 'clientv2-queue-when-reminder' });
    case QUEUE_SIGNALS.BIRTHDAY_IMMINENT:
    case QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH:
      return intl.formatMessage({ id: 'clientv2-queue-when-birthday' }, { days: daysUntil ?? 0 });
    case QUEUE_SIGNALS.POLICY_DUE_SOON:
      return intl.formatMessage({ id: 'clientv2-queue-when-policy' }, { days: daysUntil ?? 0 });
    default:
      return daysSince !== undefined
        ? intl.formatMessage({ id: 'clientv2-queue-when-no-see' }, { days: daysSince })
        : entry.meta.date || '';
  }
};

/**
 * What to do about it. The card used to repeat the signal three times (label,
 * line under the name, and 「{signal} · 建議優先聯絡客戶並更新跟進進度」 in the
 * box); the box now says the one thing the other lines do not: how to act.
 */
const TIP_ID: Record<QueueSignal, string> = {
  [QUEUE_SIGNALS.REMIND_TODAY]: 'clientv2-queue-tip-reminder',
  [QUEUE_SIGNALS.BIRTHDAY_IMMINENT]: 'clientv2-queue-tip-birthday-imminent',
  [QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH]: 'clientv2-queue-tip-birthday-month',
  [QUEUE_SIGNALS.POLICY_DUE_SOON]: 'clientv2-queue-tip-policy',
  [QUEUE_SIGNALS.LONG_TIME_NO_SEE]: 'clientv2-queue-tip-no-see'
};

const TodayQueue = ({
  entries,
  adviserEmail,
  onSelectClient,
  fillIn,
  onFillIn,
  onSnoozeFillIn,
  intl
}: TodayQueueProps) => {
  const [expanded, setExpanded] = useState(false);
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  // The 服務方式 line reads each client's notes; an import made in the drawer
  // has to show here without waiting for the page to re-render for another reason.
  const [, setNotesVersion] = useState(0);
  useEffect(() => {
    const onNotes = () => setNotesVersion(version => version + 1);
    window.addEventListener(CLIENT_NOTES_CHANGED_EVENT, onNotes);
    return () => window.removeEventListener(CLIENT_NOTES_CHANGED_EVENT, onNotes);
  }, []);
  const focusEntries = entries.slice(0, QUEUE_PREVIEW_SIZE);
  // Only into a free slot, and only one.
  const fill = fillIn && onFillIn && focusEntries.length < QUEUE_PREVIEW_SIZE ? fillIn : null;
  const fillKey = fill ? `fill-${fill.client._id}` : '';
  const taskCount = focusEntries.length + (fill ? 1 : 0);
  const completedCount =
    focusEntries.filter(entry => completed[entryKeyOf(entry)]).length + (fill && completed[fillKey] ? 1 : 0);
  const progress = taskCount ? Math.round((completedCount / taskCount) * 100) : 0;
  // The percentage counts up (same easing as the stat tiles), and the bar fill
  // is fed the animated value so the number and the width move together.
  const animatedProgress = useCountUp(progress, 800);
  // All follow-ups done: the one moment this page gets to say "打完收工".
  // Purely derived from the same `completed` state that drives the meter, so
  // it can never disagree with the numbers beside it.
  const allDone = taskCount > 0 && completedCount === taskCount;
  const message = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);

  // The whole head toggles expansion (the chevron alone was too small a
  // target). The header is deliberately NOT role="button": the chevron button
  // stays the single keyboard/AT path with aria-expanded/aria-controls, and a
  // second widget announcing the same action would read as duplication.
  const expandable = taskCount > 0;
  const toggleExpanded = () => {
    if (expandable) setExpanded(value => !value);
  };

  const fillTask = (item: FactsFillIn) => {
    const name = clientNameOf(item.client) || '-';
    const factsheet = adviserFactsheetOf(item.client, adviserEmail);
    const phone = factsheet?.phoneNumber || item.client.phoneNumber || '';
    const tier = tierMetaOf(item.client, adviserEmail);
    const isDone = Boolean(completed[fillKey]);
    const label = message('clientv2-queue-signal-fill');
    const when = message('clientv2-queue-when-fill', { field: message(FILL_IN_FIELD_ID[item.field]) });
    return (
      <article data-testid="queue-fill-task" className={`clientv2-queue-task${isDone ? ' is-done' : ''}`} key={fillKey}>
        <div className="clientv2-queue-task-top">
          <span className="clientv2-queue-signal" style={{ color: FILL_IN_COLOR }}>
            <i style={{ backgroundColor: FILL_IN_COLOR }} />
            {label}
          </span>
          <button
            type="button"
            role="checkbox"
            aria-checked={isDone}
            className={`clientv2-queue-due${isDone ? ' is-done' : ''}`}
            aria-label={message(isDone ? 'clientv2-queue-mark-pending' : 'clientv2-queue-mark-done', { name })}
            onClick={() => setCompleted(value => ({ ...value, [fillKey]: !isDone }))}
          >
            {isDone ? <SquareCheck size={15} aria-hidden="true" /> : <Square size={15} aria-hidden="true" />}
            {message(isDone ? 'clientv2-queue-done' : 'clientv2-queue-mark')}
          </button>
        </div>

        <button
          type="button"
          data-testid="queue-fill-row"
          className="clientv2-queue-client"
          aria-label={`${label}: ${name}, ${when}`}
          onClick={() => onSelectClient(item.client._id)}
        >
          <span className="clientv2-row-avatar-wrap" aria-hidden="true">
            <span className={`clientv2-row-avatar clientv2-av-${avatarGenderClass(item.client, adviserEmail)}`}>
              {name.charAt(0)}
            </span>
            {tier && <span className={`clientv2-row-tier tier-${tier.cls}`}>{tier.key}</span>}
          </span>
          <span className="clientv2-queue-client-copy">
            <span className="clientv2-queue-name">{name}</span>
            <span className="clientv2-queue-when">{when}</span>
          </span>
        </button>

        {/* What answering BUYS. 生日 buys more than the rest: 智能 IDEA reads it too. */}
        <p className="clientv2-queue-reason">
          {message(item.field === 'dob' ? 'clientv2-queue-tip-fill-dob' : 'clientv2-queue-tip-fill')}
        </p>

        <div className="clientv2-queue-task-foot">
          <span>{phone}</span>
          <div className="clientv2-queue-foot-actions">
            {onSnoozeFillIn && (
              <button
                type="button"
                data-testid="queue-fill-snooze"
                className="clientv2-queue-snooze"
                onClick={() => onSnoozeFillIn(item)}
              >
                {message('clientv2-queue-fill-snooze')}
              </button>
            )}
            <button
              type="button"
              data-testid="queue-fill-cta"
              className="clientv2-queue-follow"
              onClick={() => onFillIn?.(item)}
            >
              {message('clientv2-queue-fill-cta')}
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section className="clientv2-card clientv2-queue" aria-labelledby="clientv2-queue-title">
      <header
        className={`clientv2-queue-head${expandable ? ' is-expandable' : ''}`}
        data-testid="queue-head"
        onClick={toggleExpanded}
      >
        <div className="clientv2-queue-heading">
          <span className="clientv2-queue-heading-icon" aria-hidden="true">
            <CalendarCheck2 size={20} />
          </span>
          <span>
            <span className="clientv2-queue-title" id="clientv2-queue-title">
              {message('clientv2-queue-title')}
              {/* The completion chip REPLACES the 進度 line below rather than
                  joining it. At 3/3 the two say the same thing twice, and the
                  chip is the better half: 「今日全部完成」 is the news, 「進度
                  3 / 3」 is the arithmetic behind it. The chip also lands on the
                  title's own line, so the head reads as one statement instead
                  of a headline with a redundant subtitle under it. */}
              {allDone && (
                <span className="clientv2-queue-celebrate" data-testid="queue-celebrate">
                  <PartyPopper size={12} aria-hidden="true" />
                  {message('clientv2-queue-all-done')}
                </span>
              )}
            </span>
            {/* The count, while there is still a count worth giving. The
                percentage meter opposite keeps the number at 100% for anyone
                who wants it — that one is a bar, so it has something to show
                even when the fraction has nothing left to say. */}
            {!allDone && (
              <span className="clientv2-queue-progress-label" data-testid="queue-total">
                {message('clientv2-queue-progress', {
                  done: completedCount,
                  total: taskCount
                })}
              </span>
            )}
          </span>
        </div>

        {/* Both readouts, on every screen size. They say the same count two ways
            — a fraction under the title, a bar and a percentage opposite — and
            the phone used to drop the bar to keep the head on one row. It keeps
            both now; the row that has room for it is the one this card is. */}
        <div className="clientv2-queue-controls">
          <span className={`clientv2-queue-progress${allDone ? ' is-complete' : ''}`} aria-hidden="true">
            <span>
              <i style={{ width: `${animatedProgress}%` }} />
            </span>
            <b>{animatedProgress}%</b>
          </span>
          <button
            type="button"
            className="clientv2-queue-control"
            data-testid="queue-expand"
            aria-label={message(expanded ? 'clientv2-queue-collapse' : 'clientv2-queue-expand')}
            title={message(expanded ? 'clientv2-queue-collapse' : 'clientv2-queue-expand')}
            aria-expanded={expanded}
            aria-controls="clientv2-queue-tasks"
            disabled={!taskCount}
            onClick={event => {
              // stopPropagation, or the bubbling header click would toggle a
              // second time and the button would appear to do nothing.
              event.stopPropagation();
              toggleExpanded();
            }}
          >
            {expanded ? <ChevronUp size={17} aria-hidden="true" /> : <ChevronDown size={17} aria-hidden="true" />}
          </button>
        </div>
      </header>

      {!taskCount ? (
        <p data-testid="queue-empty" className="clientv2-queue-empty">
          {message('clientv2-queue-empty')}
        </p>
      ) : (
        <div
          id="clientv2-queue-tasks"
          className={`clientv2-queue-tasks-wrap${expanded ? ' is-open' : ''}`}
          aria-hidden={!expanded}
        >
          <div className="clientv2-queue-items">
            {focusEntries.map(entry => {
              const factsheet = adviserFactsheetOf(entry.client, adviserEmail);
              const name = clientNameOf(entry.client) || '-';
              const phone = factsheet?.phoneNumber || entry.client.phoneNumber || '';
              const tier = tierMetaOf(entry.client, adviserEmail);
              const key = entryKeyOf(entry);
              const isDone = Boolean(completed[key]);
              const signal = message(SIGNAL_MESSAGE_ID[entry.signal]);
              const when = whenTextOf(entry, intl);
              const prefs = servicePrefsSummary(readServicePrefs(entry.client._id), quizLangOf(intl.locale));

              return (
                <article data-testid="queue-task" className="clientv2-queue-task" key={key}>
                  <div className="clientv2-queue-task-top">
                    <span className="clientv2-queue-signal" style={{ color: SIGNAL_COLOR[entry.signal] }}>
                      <i style={{ backgroundColor: SIGNAL_COLOR[entry.signal] }} />
                      {message(SIGNAL_LABEL_ID[entry.signal])}
                    </span>
                    {/* A tick box, and it now says so in both channels.
                        It was a borderless grey 「○ 今日」 — no box, no fill, no
                        hit shape — which is what a DATE LABEL looks like in this
                        panel, not a control. The only thing that said otherwise
                        was the aria-label, which sighted advisers never meet.

                        `role="checkbox"` + `aria-checked` for the same reason:
                        a plain button announces 「標記 X 已跟進」 as a one-way
                        action, when the thing is a two-state option the adviser
                        can also untick. The empty/filled square, the border and
                        the hover preview (styles.scss) carry the same fact
                        visually. */}
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isDone}
                      className={`clientv2-queue-due${isDone ? ' is-done' : ''}`}
                      aria-label={message(isDone ? 'clientv2-queue-mark-pending' : 'clientv2-queue-mark-done', {
                        name
                      })}
                      onClick={() => setCompleted(value => ({ ...value, [key]: !isDone }))}
                    >
                      {isDone ? <SquareCheck size={15} aria-hidden="true" /> : <Square size={15} aria-hidden="true" />}
                      {/* 剔格只說按下去會變成什麼 —— 日子搬到名字底下。 */}
                      {message(isDone ? 'clientv2-queue-done' : 'clientv2-queue-mark')}
                    </button>
                  </div>

                  <button
                    type="button"
                    data-testid="queue-row"
                    className="clientv2-queue-client"
                    aria-label={`${signal}: ${name}, ${when}`}
                    onClick={() => onSelectClient(entry.client._id)}
                  >
                    {/* Same avatar-corner tier badge as the client row. */}
                    <span className="clientv2-row-avatar-wrap" aria-hidden="true">
                      <span
                        className={`clientv2-row-avatar clientv2-av-${avatarGenderClass(entry.client, adviserEmail)}`}
                      >
                        {name.charAt(0)}
                      </span>
                      {tier && <span className={`clientv2-row-tier tier-${tier.cls}`}>{tier.key}</span>}
                    </span>
                    <span className="clientv2-queue-client-copy">
                      <span className="clientv2-queue-name">{name}</span>
                      <span className="clientv2-queue-when">{when}</span>
                    </span>
                  </button>

                  <p className="clientv2-queue-reason">
                    {message(TIP_ID[entry.signal])}
                    {prefs && (
                      <span className="clientv2-queue-prefs" data-testid="queue-service-prefs">
                        {message('clientv2-queue-service-prefs', { prefs })}
                      </span>
                    )}
                  </p>

                  <div className="clientv2-queue-task-foot">
                    <span>{phone}</span>
                    <button
                      type="button"
                      className="clientv2-queue-follow"
                      onClick={() => onSelectClient(entry.client._id)}
                    >
                      {message('clientv2-queue-follow')}
                    </button>
                  </div>
                </article>
              );
            })}
            {fill && fillTask(fill)}
          </div>
        </div>
      )}
    </section>
  );
};

export default injectIntl(TodayQueue);
